import { useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
// eslint-disable-next-line no-restricted-imports
import { Header, Segment, Table } from 'semantic-ui-react';
import { WsModal } from '../../common';

// Import components.
import { updateMoxitOrganization } from '../../redux/actions/organizationActions';
import DocumentUploader from './DocumentUploader';

import { Label } from '@wonderschool/common-base-ui';
import { formatLocaleDate } from '../../helpers/dates';
import { formatFullName, humanReadableFileSize } from '../../helpers/utils';
import useRooms from '../../hooks/useRooms';
import SectionCard from '../Shared/SectionCard';
import ImportDataModalResult from './ImportStudentDataResult';
import StudentsCSVPlugin from './StudentsCSVPlugin';
import UppyCSV from './UppyCSV';
import studentsOnboardingCSV from './assets/wonderschool-onboarding-student.csv';

// This is the only way I could figure how to pass shouldInvite to the plugin.
let shouldInviteGlobal = false;
const shouldInviteCallback = () => shouldInviteGlobal;

const StudentsImporter = ({ currentOrganization, user, updateMoxitOrganization }) => {
  const [uploaded, setUploaded] = useState(false);
  const [shouldInvite, setShouldInvite] = useState(shouldInviteGlobal);
  const [isShowModalImportStudentDetails, setIsShowModalImportStudentDetails] = useState(false);
  const [resultData, setResultData] = useState(null);
  const rooms = useRooms();

  const { t } = useTranslation();

  const uppyPlugins = useMemo(() => {
    return [
      {
        plugin: UppyCSV,
        options: {
          csvTemplate: studentsOnboardingCSV,
          setResultData: setResultData,
          setIsShowModalImportStudentDetails: setIsShowModalImportStudentDetails,
        },
        restrictions: {
          allowedFileTypes: ['.csv', 'text/csv'],
        },
      },
      {
        plugin: StudentsCSVPlugin,
        options: {
          shouldInviteCallback: shouldInviteCallback,
          setResultData: setResultData,
          setIsShowModalImportStudentDetails: setIsShowModalImportStudentDetails,
          rooms: rooms,
        },
      },
    ];
  }, [rooms]);

  // Set upload path.
  const uploadPath = `organizations/${currentOrganization.id}/imports`;

  const onUploaded = async (successful) => {
    if (successful && successful.length) {
      const { uid, email } = user;

      // Set is uploaded flag to true.
      setUploaded(true);
      setIsShowModalImportStudentDetails(true);

      const imports = currentOrganization?.imports || [];

      successful.forEach((uploaded) => {
        const name = uploaded.name ? uploaded.name.split('.')[0] : t('students.import.unknownName');
        const id = uploaded?.meta?.refId?.split('.')[0];

        imports.push({
          id,
          name,
          path: uploadPath,
          type: uploaded.type,
          size: uploaded.size,
          extension: uploaded.extension,
          progress: uploaded.progress,
          meta: uploaded.meta,
          downloadUrl: uploaded.downloadUrl,
          uploadedBy: { uid, email, name: formatFullName(user, true) },
          uploadedDate: Date.now(),
          tags: [],
          status: t('students.import.processingStatus'),
        });
      });

      try {
        await updateMoxitOrganization({ ...currentOrganization, imports });
      } catch (error) {
        console.error(error);
      }
    }
  };

  const renderUploadedList = () => {
    const { imports } = currentOrganization;

    if (!imports || !imports.length) {
      return null;
    }

    return (
      <SectionCard header={t('students.import.history.title')}>
        <Table basic="very">
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell>{t('students.import.history.fileNameColumn')}</Table.HeaderCell>
              <Table.HeaderCell>{t('students.import.history.uploadedByColumn')}</Table.HeaderCell>
              <Table.HeaderCell>{t('students.import.history.dateColumn')}</Table.HeaderCell>
            </Table.Row>
          </Table.Header>

          <Table.Body>
            {imports.map((importItem, index) => (
              <Table.Row key={index}>
                <Table.Cell>
                  <a href={importItem?.downloadUrl} alt={importItem?.name}>
                    {importItem?.name}
                  </a>{' '}
                  <div className="text-muted">
                    {humanReadableFileSize(importItem?.size)} &middot; {importItem?.meta?.refId}
                  </div>
                </Table.Cell>
                <Table.Cell>{importItem?.uploadedBy?.name}</Table.Cell>
                <Table.Cell>{formatLocaleDate(importItem?.uploadedDate)}</Table.Cell>
              </Table.Row>
            ))}
          </Table.Body>
        </Table>
      </SectionCard>
    );
  };

  const renderPageTitle = () => {
    return (
      <Segment basic clearing>
        <Header as="h1" content={t('students.import.pageTitle')} />
      </Segment>
    );
  };

  return (
    <div className="container mx-auto">
      {/* Page Title */}
      <div className="flex">{renderPageTitle()}</div>

      {/* Conditional rendering for file upload steps or success message */}
      {!uploaded ? (
        <div className="border-color:text-gray-800 grid grid-cols-1 items-center gap-8 border-2 p-4 md:grid-cols-2">
          {/* Instructions List */}
          <div className="flex flex-col">
            <div className="flex flex-col">
              <div className="my-2">
                <div className="flex flex-row gap-2">
                  <Label extraClasses="font-bold text-md">1</Label>
                  <Label extraClasses="font-bold text-md">{t('students.import.instructions.downloadTitle')}</Label>
                </div>
                <div className="ml-3">
                  <Trans i18nKey="students.import.instructions.downloadDescription">
                    To begin the importing process, download the{' '}
                    <a href={studentsOnboardingCSV} className="text-blue-500">
                      CSV template
                    </a>
                    .
                  </Trans>
                </div>
              </div>
              <div className="my-2">
                <div className="flex flex-row gap-2">
                  <Label extraClasses="font-bold text-md">2</Label>
                  <Label extraClasses="font-bold text-md">{t('students.import.instructions.studentInfoTitle')}</Label>
                </div>
                <div className="ml-3">{t('students.import.instructions.studentInfoDescription')}</div>
              </div>
              <div className="my-2">
                <div className="flex flex-row gap-2">
                  <Label extraClasses="font-bold text-md">3</Label>
                  <Label extraClasses="font-bold text-md">{t('students.import.instructions.invitationTitle')}</Label>
                </div>
                <div className="ml-3">{t('students.import.instructions.invitationDescription')}</div>
              </div>
              <div className="my-2">
                <div className="flex flex-row gap-2">
                  <Label extraClasses="font-bold text-md">4</Label>
                  <Label extraClasses="font-bold text-md">{t('common.uploadFile')}</Label>
                </div>
                <div className="ml-3">
                  <Label extraClasses="text-md">{t('students.import.instructions.uploadDescription')}</Label>
                </div>
              </div>
            </div>

            <p className="mt-4 text-gray-500">{t('students.import.instructions.processingNote')}</p>
          </div>

          {/* File Upload Area */}
          <div className="rounded-md bg-gray-100 p-4 shadow-md">
            <div className="border-color:text-gray-800 m-4 mb-4 flex flex-row content-center gap-4 border-2 bg-white p-4">
              <input
                type="checkbox"
                className="form-checkbox mt-6 h-4 w-4 text-blue-600"
                checked={shouldInvite}
                onChange={(_e) => {
                  setShouldInvite(!_e.target.checked);
                  shouldInviteGlobal = !_e.target.checked;
                }}
              />
              <Label extraClasses="inline-flex items-center text-md">{t('students.import.sendInvitationsLabel')}</Label>
            </div>

            <div className="flex flex-col items-center text-center">
              <Label extraClasses="text-lg font-semibold">{t('students.import.uploadTitle')}</Label>
              <div className="mt-4">
                <DocumentUploader
                  uploadPath={uploadPath}
                  title={t('students.import.uploadingTitle')}
                  allowedFileTypes={['.csv', 'text/csv']}
                  onUploaded={onUploaded}
                  maxNumberOfFiles={1}
                  plugins={uppyPlugins}
                  isShowModalImportStudentDetails={isShowModalImportStudentDetails}
                />
              </div>
            </div>
          </div>
        </div>
      ) : (
        /* Success message */
        <div className="flex flex-col items-center justify-center text-center">
          <div className="rounded-full bg-green-500 p-4 text-white">
            <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h2 className="mt-2 text-2xl font-bold">{t('students.import.success.title')}</h2>
          <p className="mt-2 text-gray-500">{t('students.import.success.description')}</p>
        </div>
      )}

      {/* Uploaded List */}
      <div className="mt-8">{renderUploadedList()}</div>

      {/* Help Section */}
      <div className="mt-8 text-center">
        <Trans
          i18nKey="students.import.needHelp"
          components={{
            // eslint-disable-next-line jsx-a11y/anchor-has-content
            a: <a href="mailto:hello@wonderschool.com" className="text-blue-500 underline" />,
          }}
        />
      </div>

      {/* Import Data Modal */}
      <WsModal
        isOpen={isShowModalImportStudentDetails}
        onClose={() => setIsShowModalImportStudentDetails(false)}
        title={t('students.import.reportTitle')}
      >
        <div className="flex w-96 flex-col justify-center gap-2">
          <ImportDataModalResult resultData={resultData} />
        </div>
      </WsModal>
    </div>
  );
};

const mapStateToProps = (state) => ({
  user: state.user,
  currentOrganization: state.organizations.currentOrganization,
});

export default connect(mapStateToProps, { updateMoxitOrganization })(StudentsImporter);
